import Vue from 'vue';
import * as d3 from 'd3';

import Array_Average from './Array_Average';
import Quartile from './Quartile';
import Array_Stdev from './Array_Stdev';



const updateChangeDrivers = (periodStartInclZeroIndexed, periodEndInclZeroIndexed, nodeID, activeModule, modules, dataMatrix) => {

  /* 
  
    This function is supposed to trigger a new calculation of the change drivers
    for a selected contiguous period of time (selected in the node popup).

    This needs to be updated on at least two occasions:
    (a) On compile (i.e. when parameters / values / number of time periods have been changed)
    (b) When the user selects a different time period in the popup.

    This function needs to perform the following steps:

    (a) Obtain the selected periods
    (b) Obtain the global node data array
    (c) From the selected node as starting point looking upstream:
        Recursively analyse how 100% points of the change need to be attributed to nodes.
        For this, the function needs to consider the operation performed along the way.

    // Node.id and currently active module should allow us to uniquely identify the current node

  */


  /* 
    Compute a new array that sums up the values for the selected periods
    Here we create a single array that is the total across all time periods / slices
    Since we are dealing with samples, we just add up the i-th element across all periods
  */

  // sum function could be simplified using map()
  // We assume here that all arrays MUST have the same length (which should be the case)
  let valTotals = [];
  for( var j = 0; j < dataMatrix[0].length; j++){
    // Iterate over all elements in the period-specific array (e.g. 10000 values)
    let element_sum = 0;
    for( var i = periodStartInclZeroIndexed; i <= periodEndInclZeroIndexed; i++){
      // Iterate over the periods (matrix.length is the number of periods)
      element_sum += dataMatrix[i][j]
      //console.log('Adding ' + matrix[i][j] + ' to element ' + j + ' in period ' + i);
    }
    valTotals.push(element_sum);
  }

  // Now, we can compute updated stats on the summed up values
  let valTotalsMedianFormattedString = new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}).format(Quartile(valTotals, 0.5));
  let valTotalsQuartile25FormattedString = new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}).format(Quartile(valTotals, 0.25));
  let valTotalsQuartile75FormattedString = new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}).format(Quartile(valTotals, 0.75));
  
  let valTotalsMeanFormattedString = new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}).format(Array_Average(valTotals));
  let valTotalsStdevFormattedString = new Intl.NumberFormat('en-US', {maximumFractionDigits: 2}).format(Array_Stdev(valTotals));



  // Vue.prototype.$globalSharedData.globalNodeData


  // console.log('periodStartInclZeroIndexed', periodStartInclZeroIndexed)
  // console.log('periodEndInclZeroIndexed', periodEndInclZeroIndexed)
  // console.log('nodeID', nodeID)
  // console.log('activeModule', activeModule)
  // console.log('globalNodeData', Vue.prototype.$globalSharedData.globalNodeData)
  // console.log('modules', modules)

  // Create a dictionary of node IDs and the name of the module they're in
  // This allows the TabView to als show the module in the overview
  // For this, we parse the modules object of the editor
  // The structure of modules is:
  // (1) object with module names as keys
  // (2) each module has one data object
  // (3) each data object has a nodes object
  // (4) each nodes object has the nodes with the id being the key; and the key is also in the node object again

  let nodeToModuleNameDict = {};
  for (let [moduleNameAsKey, moduleObject] of Object.entries(modules)) {
    // Iterate over nodes in the data.nodes object of a module
    for (let [nodeKey, nodeObject] of Object.entries(moduleObject.data.nodes)) {
      // Add node with the module name to the dictionary
      nodeToModuleNameDict[nodeKey] = moduleNameAsKey;
    }
  }

  // console.log('node-to-module dict', nodeToModuleNameDict);

  // Identify the UUID of the current node
  let UUIDofCurrentNode;
  // Now, get the node's UUID given that we know which module is open right now
  for (let [nodeUUID, nodeData] of Object.entries(Vue.prototype.$globalSharedData.globalNodeData)) {
    // console.log(`${nodeUUID}:`, nodeData);
    if ( (nodeData.nodeID == nodeID) && (nodeToModuleNameDict[nodeData.nodeID] == activeModule)){
      UUIDofCurrentNode = nodeUUID;
    }
  }
  // console.log('This is the current node\'s UUID', UUIDofCurrentNode);


  // Recursive function
  let traverseTree = (currentNodeUUID, remainingShareOfOverallChangeToBeAllocated) => {

    // console.log('currentNodeUUID', currentNodeUUID);
    // console.log('remainingShareOfOverallChangeToBeAllocated', remainingShareOfOverallChangeToBeAllocated);

    // Write the attributed change into the global node array (TODO: Maybe this is not the right location to store it?)
    // Maybe this needs to be broadcast to the VueDistPreview with node.ID?
    Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID]['attributedShareOfChange'] = remainingShareOfOverallChangeToBeAllocated;

    let predecessors = Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].predecessors;

    // If there are predecessors that we need to consider
    if (predecessors.length > 0){

      let arrayOfChangeAttributedToPredecessors = [];

      // Get stats for this node
      let currentNodesMetricStartPeriod = Array_Average(Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].data[periodStartInclZeroIndexed]);
      let currentNodesMetricEndPeriod = Array_Average(Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].data[periodEndInclZeroIndexed]);
      let currentNodesDeltaAbs = currentNodesMetricEndPeriod - currentNodesMetricStartPeriod;
      let currentNodesGrowth = (currentNodesMetricEndPeriod - currentNodesMetricStartPeriod) / currentNodesMetricStartPeriod;

      // console.log('Stats for current Node:')
      // console.log('currentNodesGrowth', currentNodesGrowth)
  
      /***********************************************************************************  
        What is the growth data for each predecessor (in order of the predecessors array)
      **********************************************************************************/

      let predecessorGrowthData = [];
      predecessors.forEach(function(predecessorUUID){
        // console.log(predecessorUUID);
        // Get the array average of this predecessor node's data array at the start period
        let metricStartPeriod = Array_Average(Vue.prototype.$globalSharedData.globalNodeData[predecessorUUID].data[periodStartInclZeroIndexed])
        let metricEndPeriod = Array_Average(Vue.prototype.$globalSharedData.globalNodeData[predecessorUUID].data[periodEndInclZeroIndexed])
        let growth = (metricEndPeriod - metricStartPeriod) / metricStartPeriod
        predecessorGrowthData.push({
          metricStartPeriod: metricStartPeriod,
          metricEndPeriod: metricEndPeriod,
          growth: growth,
          delta_abs: metricEndPeriod - metricStartPeriod
        })
      });

      // console.log('predecessorGrowthData', predecessorGrowthData)


      /*****************************************************************************************************  
        Check what this node's operation is; then attribute change percentage points to each predecessor
      *****************************************************************************************************/

      if (Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].nodeFunction == "Add"){

        // console.log("Add")

        // Order does not matter
        // Obtain the attributed change
        // TODO: Is this attribution calculation correct at all?

/*         // Alte Version basierend auf absoluten Veränderungen
        predecessorGrowthData.forEach(function(nodeGrowthData){
          let attributedChange = (nodeGrowthData.growth / currentNodesGrowth) * remainingShareOfOverallChangeToBeAllocated;
          arrayOfChangeAttributedToPredecessors.push(attributedChange)
        }); */

        // X is the share of the change that is attributed to the first element
        let X = (( (currentNodesGrowth - predecessorGrowthData[1].growth) / predecessorGrowthData[0].growth) /  (1 - (predecessorGrowthData[1].growth / predecessorGrowthData[0].growth)));
        arrayOfChangeAttributedToPredecessors.push(remainingShareOfOverallChangeToBeAllocated * X);
        arrayOfChangeAttributedToPredecessors.push(remainingShareOfOverallChangeToBeAllocated * ( 1 - X));
        // console.log('x', X)
        
        // Call the recursive function
        for (let i = 0; i < predecessors.length; i++){
          console.log(i)
          let thisPredecessorsUUID = predecessors[i];
          let thisPredecessorsAttributedChange = arrayOfChangeAttributedToPredecessors[i];
          traverseTree(thisPredecessorsUUID, thisPredecessorsAttributedChange);
        }

      } else if (Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].nodeFunction == "Subtract")  {

        // console.log("Subtract")

        // Careful: Order matters!
        // We have to assume exactly 2 predecessors here; the second one gets subtracted from first one
        if (predecessors.length == 2){

          traverseTree(predecessors[0], (predecessorGrowthData[0].delta_abs / currentNodesDeltaAbs) * remainingShareOfOverallChangeToBeAllocated);
          traverseTree(predecessors[1], ((-1 * predecessorGrowthData[1].delta_abs) / currentNodesDeltaAbs) * remainingShareOfOverallChangeToBeAllocated); // we negate the delta since this was deducted
          
        } else {
          console.log('A sutract node in the updateCHangeDrivers had more than 2 predecessors.');
        }


      } else if (Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].nodeFunction == "Multiply")  {

        // console.log("Multiply")

        // Order does not matter
        let q = ((1 + currentNodesGrowth) / ( (1 + predecessorGrowthData[0].growth) * (1 + predecessorGrowthData[1].growth) ));
        let X1 = ( - 0.5 + Math.sqrt( 0.25 + q));
        let X2 = ( - 0.5 - Math.sqrt( 0.25 + q));
        
        // console.log('q', q);
        // console.log('X1', X1);
        // console.log('X2', X2);

        // Call the recursive function
        for (let i = 0; i < predecessors.length; i++){
          let thisPredecessorsUUID = predecessors[i];
          let thisPredecessorsAttributedChange = arrayOfChangeAttributedToPredecessors[i];
          traverseTree(thisPredecessorsUUID, thisPredecessorsAttributedChange);
        }

      } else if (Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].nodeFunction == "Divide")  {

        // console.log("Divide")

        // Call the recursive function
        for (let i = 0; i < predecessors.length; i++){
          let thisPredecessorsUUID = predecessors[i];
          let thisPredecessorsAttributedChange = arrayOfChangeAttributedToPredecessors[i];
          traverseTree(thisPredecessorsUUID, thisPredecessorsAttributedChange);
        }
      
      } else if (Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].nodeFunction == "Power")  {

        // console.log("Power")

        // Call the recursive function
        for (let i = 0; i < predecessors.length; i++){
          let thisPredecessorsUUID = predecessors[i];
          let thisPredecessorsAttributedChange = arrayOfChangeAttributedToPredecessors[i];
          traverseTree(thisPredecessorsUUID, thisPredecessorsAttributedChange);
        }
      
      } else if (Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].nodeFunction == "Mixer")  {

        // console.log("Mixer")

        // Call the recursive function
        for (let i = 0; i < predecessors.length; i++){
          let thisPredecessorsUUID = predecessors[i];
          let thisPredecessorsAttributedChange = arrayOfChangeAttributedToPredecessors[i];
          traverseTree(thisPredecessorsUUID, thisPredecessorsAttributedChange);
        }

      } else {
        console.log('Node type is recognised')
        console.log(Vue.prototype.$globalSharedData.globalNodeData[currentNodeUUID].nodeFunction)
      }


    }

    
  }

  // Start to traverse the model starting at the current node and with 100% change to be attributed
  // TODO: Do we need to track if the change was positive or negative to reflect this in the DistPreviews
  traverseTree(UUIDofCurrentNode, 1)


  let result = {
    valTotalsMedianFormattedString: valTotalsMedianFormattedString,
    valTotalsQuartile25FormattedString: valTotalsQuartile25FormattedString,
    valTotalsQuartile75FormattedString: valTotalsQuartile75FormattedString,
    valTotalsMeanFormattedString: valTotalsMeanFormattedString,
    valTotalsStdevFormattedString: valTotalsStdevFormattedString,
  }

  return result;


}


export default updateChangeDrivers;

  

  